import { Config } from "@fortawesome/fontawesome-svg-core";
import { PreventAndRedirectCommands, Router, RouterLocation } from "@vaadin/router";
import cronstrue from "cronstrue";
import { css, html, LitElement } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { styleMap } from "lit/directives/style-map.js";
import { when } from "lit/directives/when.js";
import { ToasterService } from "se-shared/services/toaster.service";
import { container } from "tsyringe";
import { LogLevel } from "../../enums/log-level";
import { LogMode } from "../../enums/log-mode";
import { ParallelExport } from "../../enums/parallel-export";
import { ScheduleType } from "../../enums/schedule-type";
import { ConfigRunPageModel } from "../../models/config-run-model";
import { ScheduleRunWithProxyViewModel } from "../../models/schedule-run-model";
import { ServerGroup } from "../../models/server-group";
import { AuthService } from "../../services/auth.service";
import { ConfigService } from "../../services/config.service";
import { ModalDialogService } from "../../services/modal-editor.service";
import { ProxyService } from "../../services/proxy.service";
import { RunService } from "../../services/run.service";
import { UserState } from "../../services/user.state";
import { CheckboxEditorElement } from "../editors/checkbox-editor.element";
import { DateEditorElement } from "../editors/date-editor.element";
import { InputEditorElement } from "../editors/input-editor.element";
import { NumberEditorElement } from "../editors/number-editor.element";
import { RadioEditorElement } from "../editors/radio-editor.element";
import { SelectEditorElement } from "../editors/select-editor.element";
import { ParameterEditorElement } from "./parameter-editor.element";
import { SubscriptionPlanApi } from "../../services/subscription-plan-api";
import "../components/cards/edit-form-card.element";
import "../components/cards/card-buttons.element";
import { FormInputEditorElement } from "../editors/form/form-input-editor.element";
import { FormSelectEditorElement } from "../editors/form/form-select-editor.element";
import { FormNumberEditorElement } from "../editors/form/form-number-editor.element";
import { FormDateTimeEditorElement } from "../editors/form/form-datetime-editor.element";
import { FormToggleEditorElement } from "../editors/form/form-toggle-editor.element";

@customElement("se-start-run")
export class SeStartRunElement extends LitElement {
    private _modalService: ModalDialogService;
    private _proxyService: ProxyService;
    private _authService: AuthService;
    private _runService: RunService;
    private _configService: ConfigService;
    private _toaterService: ToasterService;
    private _subscriptionPlanApi: SubscriptionPlanApi;
    private _config: Config;
    private _userState: UserState;
    private _runModel: ConfigRunPageModel;
    private _serverGroups: ServerGroup[];
    //private _parameters: { [name: string]: string };
    private _taskId: number;
    private _configId: number;
    private _isNew = false;
    private _task?: ScheduleRunWithProxyViewModel = null;

    @state() private _mode: 'run' | 'schedule' = 'run'; // Default mode
    @state() private _isParallelism = false;
    private _selectedParallelMaxConcurrency?: number;
    private _selectedParallelExport = ParallelExport.Combined;
    private _selectedParallelism = 1;
    private _selectedLogLevel = LogLevel.Info;
    private _selectedLogMode = LogMode.Text;
    private _selectedProxyPoolId = -1;
    private _selectedServerGroupId = 0;

    @state() private _isProxyRequired = true;
    @state() private _showscheduleType = false;
    @state() private _runOnceOption = false;
    @state() private _runEveryOption = false;
    @state() private _CRONString = "";
    @state() private _CRONOption = false;
    @state() private _proxyPools: any[] = [];
    @state() private _dateTime: Date;
    @state() private _runEveryPeriodValue = "1";
    @state() private _spaceName: string;
    @state() private _configName: string;
    @state() private _isStartDateInPast = false; // New state variable

    @state() private _isLoading = true;

    @query("#yes") private _radioEditor: RadioEditorElement;
    @query("#yes") private _mapEditor: FormDateTimeEditorElement;
    @query("#server") private _serverEditor: FormSelectEditorElement;
    @query("#scheduleType") private _scheduleType: FormSelectEditorElement;
    @query("#task") private _taskEditor: CheckboxEditorElement;
    @query("#scheduleOnOff") private _scheduleOnOff: FormToggleEditorElement;
    @query("#proxyPools") private _proxyPoolsSelect: FormSelectEditorElement;
    @query("#taskOptions") private _taskOptionsEditor: HTMLDivElement;
    @query("#taskName") private _taskNameEditor: FormInputEditorElement;
    @query("#parallelSets") private _parallelSetsEditor: FormNumberEditorElement;
    @query("#concurrency") private _concurrencyEditor: FormNumberEditorElement;
    @query("#parallelExport") private _parallelExportEditor: FormSelectEditorElement;
    @query("#isParallelism") private _isParallelismEditor: FormToggleEditorElement;
    @query("#exclusive") private _exclusiveEditor: FormToggleEditorElement;
    @query("#waitOnFailure") private _waitOnFailureEditor: FormToggleEditorElement;
    @query("#inputParameters") private _inputParametersEditor: ParameterEditorElement;
    @query("#logLevel") private _logLevelEditor: FormSelectEditorElement;
    @query("#logMode") private _logModeEditor: FormSelectEditorElement;

    @query("#StartDate") private _StartDate: FormDateTimeEditorElement;
    @query("#StartHour") private _StartHour: FormInputEditorElement;
    @query("#StartMinutes") private _StartMinutes: FormInputEditorElement;
    @query("#StartPeriod") private _StartPeriod: FormSelectEditorElement;

    @query("#runEveryDate") private _runEveryDate: DateEditorElement;
    @query("#runEveryCount") private _runEveryCount: NumberEditorElement;
    @query("#runEveryPeriod") private _runEveryPeriod: FormSelectEditorElement;

    @query("#CRONMinute") private _CRONMinute: FormInputEditorElement;
    @query("#CRONHour") private _CRONHour: FormInputEditorElement;
    @query("#CRONDay") private _CRONDay: FormInputEditorElement;
    @query("#CRONMonth") private _CRONMonth: FormInputEditorElement;
    @query("#CRONWeekDay") private _CRONWeekDay: FormInputEditorElement;

    private _logLevels = Object.entries(LogLevel).map(([value]) => ({ id: value, name: value }));
    private _logModes = Object.entries(LogMode).map(([key, value]) => ({ id: key, name: value }));
    private _parallelExportOptions = Object.entries(ParallelExport).map(([key, value]) => ({ id: key, name: value }));

    private _taskProperties = {};
    constructor() {
        super();
        this._authService = container.resolve(AuthService);
        this._userState = container.resolve(UserState);
        this._configService = container.resolve(ConfigService);
        this._toaterService = container.resolve(ToasterService);
        this._runService = container.resolve(RunService);
        this._proxyService = container.resolve(ProxyService);
        this._modalService = container.resolve(ModalDialogService);
        this._subscriptionPlanApi = container.resolve(SubscriptionPlanApi);
    }

    private __scheduleType?;
    private __runEveryCount?;
    private __runEveryPeriod?;
    private __localSchedule?;
    private __startTime?: Date;
    private __taskName?;
    private __today? = this.addMinutes(new Date(), 5);
    private __timePMAM?;

    private addMinutes(date: Date, minutes: number) {
        date.setMinutes(date.getMinutes() + minutes);
        return date;
    }

    async onBeforeEnter(location: RouterLocation, commands: PreventAndRedirectCommands, router: Router) {
        if (!this._authService.isUser) {
            return commands.redirect("/login");
        }

        // Determine mode and IDs from route
        const pathname = location.pathname;
        const configIdParam = location.params.configId?.valueOf() as string;
        const taskIdParam = location.params.taskId?.valueOf() as string;

        if (configIdParam) {
             this._configId = parseInt(configIdParam);
        } else {
             console.error("Config ID is missing from the route.");
             this._toaterService.showError("Configuration ID not found in URL.");
             // Optional: Redirect or show an error state
             return commands.prevent(); // Prevent further processing if configId is crucial
        }


        if (pathname.endsWith("/run")) {
            this._mode = 'run';
            this._taskId = undefined;
            this._isNew = true; // A "run now" action is always considered 'new' in terms of scheduling
        } else if (pathname.includes("/schedule")) {
            this._mode = 'schedule';
            if (taskIdParam) {
                this._taskId = parseInt(taskIdParam);
                this._isNew = false; // Editing an existing schedule
            } else {
                this._taskId = undefined;
                this._isNew = true; // Creating a new schedule
                this._showscheduleType = true;
            }
        } else {
             console.warn("Could not determine mode from path:", pathname);
             // Default or error handling if path doesn't match expected patterns
             this._mode = 'run'; // Or potentially redirect/show error
        }

        // The rest of initialization logic will now use the correctly set _configId, _taskId, _isNew, and _mode
        // The call to prevent is moved here to ensure it always happens after processing
        commands.prevent();
        // Note: initializeAsync is called in connectedCallback, which runs after onBeforeEnter
        // If initialization depends *immediately* on mode/IDs, consider calling parts of it here
        // or ensuring connectedCallback handles the state correctly.
    }
    private addNewProxyPool() {
        Router.go("/org/proxies/new");
    }
    connectedCallback() {
        super.connectedCallback();
        document.title = this._mode === 'run' ? "Start Run" : "Schedule Run";
        this.initializeAsync();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
    }

    private async initializeAsync() {
        try {
            this.__timePMAM = this.gettimePMAM(this.__today);

            // Determine configId first
            if (this._taskId) {
                const res = await this._runService.api.getTaskAsync(this._taskId);
                if (res.isOk) {
                    this._task = res.value;
                    this._configId = this._task.configId;
                    // ... (rest of existing task handling logic) ...
                } else {
                    this._toaterService.showNetworkError(res.err);
                    // Potentially return or throw error if configId cannot be determined
                    return;
                }
            } else if (this._configId) {
                 // _configId is already set via onBeforeEnter
            } else {
                 // Handle case where neither taskId nor configId is available
                 console.error("Cannot determine configId for breadcrumbs");
                 return;
            }


            // Fetch config and space names for breadcrumbs
             const nameResult = await this._configService.api.getNameAndTypeAsync(this._configId);
             if (nameResult.isOk) {
                 this._configName = nameResult.value.name;
                 // Use selectedSpaceId from userState to determine space context
                 this._spaceName = this._userState.selectedSpaceId === 0 ? "Personal" : nameResult.value.spaceName;

                 // Assign to userState if needed (mimicking agent-details)
                 if (this._userState.selectedConfigId !== this._configId) {
                    this._userState.selectedConfigId = this._configId;
                    this._userState.selectedConfigName = this._configName; // Update userState as well
                    this._userState.selectedConfigType = nameResult.value.type;
                    this._userState.selectedConfigValidationStatus = nameResult.value.validationStatus;
                 }

             } else {
                 // Handle error fetching names - maybe show a generic name or log error
                 this._configName = "Agent";
                 this._spaceName = "Space";
                 console.error("Failed to fetch config/space name:", nameResult.err);
                 this._toaterService.showNetworkError(nameResult.err);
             }


            if (this._taskId) {
                 // Existing logic using this._task, now this._configId is guaranteed
                 if (!this._isNew) {
                     this.__scheduleType = this._task.scheduleType;
                     this.__runEveryCount = this._task.runEveryCount;
                     this.__runEveryPeriod = this._task.runEveryPeriod;
                     this.__localSchedule = this._task.localSchedule?.split(" ");
                     this.__startTime =
                         this._task.scheduleType === ScheduleType.none ? this.addMinutes(new Date(), 5) : new Date(this._task.startTime);
                     this.__taskName = this._task.taskName;
                     // remove seconds
                     this.__today = new Date(this.__startTime);
                     this.__today.setSeconds(0, 0);
                     this.__timePMAM = String(this.__today.getHours() >= 12 ? (this.__timePMAM = "12") : (this.__timePMAM = "0"));

                     // Initial check for past date when editing existing task
                     if (this.__today && this.__today.getTime() < new Date().getTime()) {
                        this._isStartDateInPast = true;
                    }

                     setTimeout(() => {
                         this.scheduleChangeEdit(this.__scheduleType);
                     }, 0);

                     this._isProxyRequired = this._authService.orgSettings.isProxyRequired && !this._task.configProxyPoolId;
                 }
            }
            const result = await this._configService.api.getRunPageModelAsync(this._configId);
            if (result.isOk) {
                this._runModel = result.value;
                this._serverGroups = this._runModel.serverGroups.serverGroups;
                //this._serverGroups.splice(0, 0, { id: 0, name: "Shared", description: "" }); //Currently not supported.
                this._serverGroups.splice(0, 0, { id: -this._authService.user.organizationId, name: "Default", description: "" });

                //Compare agent input params with dynamic params that are saved in task table.
                if (this._task?.parameters) {
                    this._taskProperties = JSON.parse(this._task.parameters);
                } else if (this._runModel.lastRunSettings?.parameters) {
                    const json = JSON.parse(this._runModel.lastRunSettings.parameters);

                    for (const key in this._runModel.inputParameters) {
                        if (json.hasOwnProperty(key)) {
                            this._runModel.inputParameters[key] = json[key];
                        }
                    }
                }
                //this._parameters = { ...this._runModel.inputParameters };
                this._isProxyRequired = this._authService.orgSettings.isProxyRequired;
            } else {
                this._toaterService.showNetworkError(result.err);
            }

            // get the list of proxy pools
            const proxyPoolsResults = await this._proxyService.api.getAllProxyPoolsWithSharedAsync(-1, -1, "null", "name", 1);
            if (proxyPoolsResults.isOk) {
                this._proxyPools = proxyPoolsResults.value.proxyPools;

                //sort proxy pools by name asc
                this._proxyPools.sort((a, b) => (a.name > b.name ? 1 : -1));

                if (this._task?.proxyPoolId && this._proxyPools.some((p) => p.id === this._task.proxyPoolId)) {
                    //default to the proxy pool from the saved task
                    this._selectedProxyPoolId = this._task.proxyPoolId;
                } else if (this._runModel?.proxyPoolId && this._proxyPools.some((p) => p.id === this._runModel.proxyPoolId)) {
                    //default to the proxy pool from the config
                    this._selectedProxyPoolId = this._runModel.proxyPoolId;
                } else if (
                    this._runModel?.lastRunSettings?.proxyPoolId &&
                    this._proxyPools.some((p) => p.id === this._runModel.lastRunSettings?.proxyPoolId)
                ) {
                    // default to the last used proxy ppol
                    this._selectedProxyPoolId = this._runModel.lastRunSettings?.proxyPoolId;
                } else if (this._isProxyRequired && this._proxyPools.length > 0) {
                    //we must have a proxy pool so there is not a None option. Default the
                    //selected id to the id of the ifrst item in the list
                    this._selectedProxyPoolId = this._proxyPools[0].id;
                } else {
                    //default to none
                    this._selectedProxyPoolId = -1;
                }
            } else {
                this._toaterService.showUnexpectedError(proxyPoolsResults.err.message);
                return;
            }

            if (this._task) {
                if (this._task.logLevel) this._selectedLogLevel = this._task.logLevel;
                if (this._task.logMode) this._selectedLogMode = this._task.logMode;
                if (this._task.parallelism) this._selectedParallelism = this._task.parallelism;
                if (this._task.parallelMaxConcurrency) this._selectedParallelMaxConcurrency = this._task.parallelMaxConcurrency;
                if (this._task.parallelExport) this._selectedParallelExport = this._task.parallelExport;
                this._selectedServerGroupId = this._task.serverGroupId;
            } else {
                if (this._runModel.lastRunSettings?.logLevel) this._selectedLogLevel = this._runModel.lastRunSettings?.logLevel;
                if (this._runModel.lastRunSettings?.logMode) this._selectedLogMode = this._runModel.lastRunSettings?.logMode;
                if (this._runModel.lastRunSettings?.parallelism) this._selectedParallelism = this._runModel.lastRunSettings?.parallelism;
                if (this._runModel.lastRunSettings?.parallelMaxConcurrency)
                    this._selectedParallelMaxConcurrency = this._runModel.lastRunSettings?.parallelMaxConcurrency;
                if (this._runModel.lastRunSettings?.parallelExport)
                    this._selectedParallelExport = this._runModel.lastRunSettings?.parallelExport;
                this._selectedServerGroupId = this._runModel.defaultServerGroupId;
            }
            this._isParallelism = this._selectedParallelism > 1;
             // Initial check for past date if creating a new task (using default __today)
             if (this._isNew && this.__today && this.__today.getTime() < new Date().getTime()) {
                 this._isStartDateInPast = true;
             }
        } finally {
            this._isLoading = false;
        }
    }

    scheduleUpdated() {
        if (this._scheduleOnOff.liveValue) {
            this._showscheduleType = true;
        } else {
            this._showscheduleType = false;
        }
    }

    reportValidity(): boolean {
        if (this._runEveryOption && this._runEveryCount) {
            if (!this._runEveryCount.reportValidity()) return false;
        }
        if (this._runOnceOption && this._StartDate) {
            if (!this._StartDate.reportValidity()) return false;
        }
        if (this._showscheduleType && this._taskNameEditor) { // Added null check
            if (!this._taskNameEditor.reportValidity()) return false;
        }
        if (this._inputParametersEditor) {
            if (!this._inputParametersEditor.reportValidity()) return false;
        }
        if (this._isParallelism) {
             if (this._parallelSetsEditor && !this._parallelSetsEditor.reportValidity()) return false; // Added null check
             if (this._concurrencyEditor && !this._concurrencyEditor.reportValidity()) return false; // Added null check
        }

        return true;
    }

    private _validateRunEveryCount() {
        if (!this._runEveryCount) return;

        const editor = this._runEveryCount;
        const value = editor.liveValue;

        editor.setCustomValidity('');

        if (value === null || value === undefined) {
             editor.setCustomValidity("Please enter a value for the 'Run Every' amount.");
        } else if (typeof value !== 'number' || isNaN(value) || !isFinite(value)) {
             editor.setCustomValidity("Please enter a valid number for the 'Run Every' amount.");
        } else if (value < 1) {
             editor.setCustomValidity("The 'Run Every' amount must be 1 or greater.");
        }

        editor.reportValidity();
    }

    async getScheduleDate(): Promise<any> {
        let result;
        let localschedule;
        let runEveryData;
        if (this._showscheduleType == false) {
            const modalResult = await this._modalService.openConfirmDialogAsync({
                title: "No Schedule",
                body: `Are you sure you want to save the task without scheduling?`,
                saveCaption: "Save",
            });
            if (modalResult.isSave) {
                return { cron: "", startDateTime: null };
            }
            return;
        } else {
            const dateTime = this._StartDate.liveValue;

            // Validate for "Run Once" schedule type
            if (this._scheduleType.liveValue == "1") {
                if (this._StartDate) {
                    const selectedDateTime = this._StartDate.liveValue;
                    if (selectedDateTime == null) {
                        this._StartDate.setCustomValidity("Please pick a start date and time for 'Run Once'.");
                        this._StartDate.reportValidity(); // Trigger validation UI
                        this._toaterService.showError("Please pick a start date and time for 'Run Once'.");
                        return undefined;
                    }

                    const now = new Date();
                    if (selectedDateTime.getTime() < now.getTime()) {
                        this._StartDate.setCustomValidity("Start date and time must be in the future.");
                        // Calling reportValidity will make the browser show a validation message (if not prevented)
                        // and allow styling via :invalid pseudo-class.
                        if (!this._StartDate.reportValidity()) {
                             this._toaterService.showError("Start date and time must be in the future. Please correct it.");
                             return undefined;
                        }
                    } else {
                        // If the date is valid (not null and not in the past), clear any custom validity.
                        this._StartDate.setCustomValidity("");
                        // Optionally, if you want to ensure the visual state is updated if it was previously invalid:
                        // this._StartDate.reportValidity(); // This might be redundant if setCustomValidity("") clears UI
                    }
                } else {
                     // This case should ideally not occur if the component is present.
                     this._toaterService.showUnexpectedError("Start Date component is not available.");
                     return undefined;
                }
            }
            
            // General check for dateTime after specific "Run Once" validation
            // This might be redundant for "Run Once" if already handled above, but good for other types if they don't set it.
            if (dateTime == null && this._scheduleType.liveValue !== "3") { // CRON doesn't strictly need _StartDate initially
                 this._toaterService.showUnexpectedError("Please pick a date and fill the necessary information.");
                 // Ensure the input is marked, though specific messages are better
                 if(this._StartDate) {
                    this._StartDate.setCustomValidity("A start date is required.");
                    this._StartDate.reportValidity();
                 }
                 return undefined;
            }


            const UTCDate = dateTime ? new Date(dateTime.getTime() + dateTime.getTimezoneOffset() * 60 * 1000) : null;
            // For "Run Once", UTCDate is derived from the now-validated dateTime
            if (this._scheduleType.liveValue == "1") {
                // At this point, dateTime is validated (not null, not in the past)
                result = this.toCron(UTCDate.getMinutes(), UTCDate.getHours(), UTCDate.getDate(), UTCDate.getMonth() + 1, UTCDate.getDay());
                localschedule = this.toCron(
                    dateTime.getMinutes(),
                    dateTime.getHours(),
                    dateTime.getDate(),
                    dateTime.getMonth() + 1,
                    dateTime.getDay()
                );
            } else if (this._scheduleType.liveValue == "2") {
                if (!this._runEveryCount.reportValidity()) {
                     return undefined;
                 }
                 const runEveryValue = this._runEveryCount.liveValue;
                 if (runEveryValue == null || runEveryValue === undefined || runEveryValue < 1) {
                      this._toaterService.showError("Please provide a valid number (>= 1) for the 'Run Every' amount.");
                      return undefined;
                 }

                runEveryData = { runEveryCount: this._runEveryCount.liveValue, runEveryPeriod: this._runEveryPeriod.liveValue };

                const obj = {
                    "1": [`*/${this._runEveryCount.liveValue}`, "*", "*", "*", "*"],
                    "2": [`${UTCDate.getMinutes()}`, `*/${this._runEveryCount.liveValue}`, "*", "*", "*"],
                    "3": [`${UTCDate.getMinutes()}`, `${UTCDate.getHours()}`, `*/${this._runEveryCount.liveValue}`, "*", "*"],
                    "4": [`${UTCDate.getMinutes()}`, `${UTCDate.getHours()}`, `*/${7 * this._runEveryCount.liveValue}`, "*", "*"],
                    "5": [`${UTCDate.getMinutes()}`, `${UTCDate.getHours()}`, "*", `*/${this._runEveryCount.liveValue}`, "*"],
                };
                const localObj = {
                    "1": [`*/${this._runEveryCount.liveValue}`, "*", "*", "*", "*"],
                    "2": [`${dateTime.getMinutes()}`, `*/${this._runEveryCount.liveValue}`, "*", "*", "*"],
                    "3": [`${dateTime.getMinutes()}`, `${dateTime.getHours()}`, `*/${this._runEveryCount.liveValue}`, "*", "*"],
                    "4": [`${dateTime.getMinutes()}`, `${dateTime.getHours()}`, `*/${7 * this._runEveryCount.liveValue}`, "*", "*"],
                    "5": [`${dateTime.getMinutes()}`, `${dateTime.getHours()}`, "*", `*/${this._runEveryCount.liveValue}`, "*"],
                };
                result = this.toCron(
                    obj[this._runEveryPeriod.liveValue][0],
                    obj[this._runEveryPeriod.liveValue][1],
                    obj[this._runEveryPeriod.liveValue][2],
                    obj[this._runEveryPeriod.liveValue][3],
                    obj[this._runEveryPeriod.liveValue][4]
                );
                localschedule = this.toCron(
                    localObj[this._runEveryPeriod.liveValue][0],
                    localObj[this._runEveryPeriod.liveValue][1],
                    localObj[this._runEveryPeriod.liveValue][2],
                    localObj[this._runEveryPeriod.liveValue][3],
                    localObj[this._runEveryPeriod.liveValue][4]
                );
            } else if (this._scheduleType.liveValue == "3") {
                //this part will take the date from the CRON fields and turn it into UTC
                //01/01/2022 00:02 UTC+1 ===> 12/31/2021 23:02 UTC
                //and turn it back into cron that will be saved in the database

                const CRONDate = new Date();

                const minutes = /^\d+$/.test(this._CRONMinute.liveValue) ? parseInt(this._CRONMinute.liveValue) : NaN;
                if (!isNaN(minutes)) {
                    CRONDate.setMinutes(minutes);
                }

                const hours = /^\d+$/.test(this._CRONHour.liveValue) ? parseInt(this._CRONHour.liveValue) : NaN;
                if (!isNaN(hours)) {
                    CRONDate.setHours(hours);
                }

                const dayOfMonth = /^\d+$/.test(this._CRONDay.liveValue) ? parseInt(this._CRONDay.liveValue) : NaN;
                if (!isNaN(dayOfMonth)) {
                    CRONDate.setDate(dayOfMonth);
                }

                const month = /^\d+$/.test(this._CRONMonth.liveValue) ? parseInt(this._CRONMonth.liveValue) : NaN;
                if (!isNaN(month)) {
                    CRONDate.setMonth(month - 1);
                }

                const CRONUTCDate = new Date(CRONDate.getTime() + CRONDate.getTimezoneOffset() * 60 * 1000);
                /////
                let dayOfWeek = /^\d+$/.test(this._CRONWeekDay.liveValue) ? parseInt(this._CRONWeekDay.liveValue) : NaN;
                let strDayOfWeek = this._CRONWeekDay.liveValue;
                if (isNaN(dayOfWeek) && this._CRONWeekDay.liveValue.includes(",")) {
                    strDayOfWeek = this._CRONWeekDay.liveValue
                        .split(",")
                        .map((num) => {
                            if (CRONUTCDate.getDay() > CRONDate.getDay()) {
                                return parseInt(num.trim()) + 1;
                            } else if (CRONUTCDate.getDay() < CRONDate.getDay()) {
                                return parseInt(num.trim()) - 1;
                            }
                        })
                        .join(",");
                } else {
                    if (CRONUTCDate.getDay() > CRONDate.getDay()) {
                        dayOfWeek++;
                    } else if (CRONUTCDate.getDay() < CRONDate.getDay()) {
                        dayOfWeek--;
                    }
                }

                result = this.toCron(
                    !isNaN(minutes) ? CRONUTCDate.getMinutes() : this._CRONMinute.liveValue,
                    !isNaN(hours) ? CRONUTCDate.getHours() : this._CRONHour.liveValue,
                    !isNaN(dayOfMonth) ? CRONUTCDate.getDate() : this._CRONDay.liveValue,
                    !isNaN(month) ? CRONUTCDate.getMonth() + 1 : this._CRONMonth.liveValue,
                    !isNaN(dayOfWeek) ? dayOfWeek : strDayOfWeek
                );
                localschedule = this.toCron(
                    this._CRONMinute.liveValue,
                    this._CRONHour.liveValue,
                    this._CRONDay.liveValue,
                    this._CRONMonth.liveValue,
                    this._CRONWeekDay.liveValue
                );

                console.log(cronstrue.toString(result));
            }
            return { cron: result, startDateTime: dateTime, localSchedule: localschedule, runEveryData };
        }
    }

    toCron(mins, hrs, dayOfMonth, month, dayOfWeek) {
        return `${mins} ${hrs} ${dayOfMonth} ${month} ${dayOfWeek}`;
    }

    async saveTaskAsync() {
        if (this.reportValidity()) {
            const dateRes = await this.getScheduleDate();
            if (dateRes == undefined) return;

            if (
                !this._isProxyRequired &&
                (!this._selectedProxyPoolId || this._selectedProxyPoolId === -1) &&
                ((!this._isNew && !this._task.configProxyPoolId) || (this._isNew && !this._runModel.proxyPoolId))
            ) {
                const dialogRes = await this._modalService.openConfirmDialogAsync({
                    title: "Save Task Without Proxy?",
                    body: "Are you sure you want to save this task without setting a proxy? The agent has no default proxies, so it will run without proxies.",
                });
                if (!dialogRes.isSave) {
                    return;
                }
            }

            this._inputParametersEditor?.save();

            const groupId = parseInt(this._serverEditor.liveValue);
            const runEveryPeriod = dateRes?.runEveryData ? dateRes.runEveryData.runEveryPeriod : null;
            const runEveryCount = dateRes?.runEveryData ? dateRes.runEveryData.runEveryCount : null;
            const serverGroupId = groupId > 0 ? groupId : undefined;

            const task = {
                runEveryPeriod: runEveryPeriod,
                runEveryCount: runEveryCount,
                schedule: dateRes.cron,
                scheduleType: this._scheduleOnOff.liveValue === false ? 0 : Number(this._scheduleType.liveValue),
                localSchedule: dateRes.localSchedule,
                startTime: dateRes.startDateTime,
                configId: this._runModel.id,
                proxyPoolId: this._runModel.proxyPoolId !== this._selectedProxyPoolId ? this._selectedProxyPoolId : -1,
                parallelism: this._isParallelism ? this._parallelSetsEditor.liveValue ?? 2 : 1,
                parallelExport: this._isParallelism ? (this._parallelExportEditor.liveValue as ParallelExport) : undefined,
                parallelMaxConcurrency: this._isParallelism ? this._concurrencyEditor.liveValue : undefined,
                serverGroupId: serverGroupId,
                parameters: JSON.stringify(this._inputParametersEditor?.properties ?? {}),
                isSaveAsTask: true,
                taskName: this._taskNameEditor.liveValue,
                isExclusive: this._exclusiveEditor.liveValue,
                isWaitOnFailure: this._waitOnFailureEditor.liveValue,
                logLevel: this._logLevelEditor.liveValue as LogLevel,
                logMode: this._logModeEditor.liveValue as LogMode,
            };

            const result = this._isNew
                ? await this._runService.api.scheduleAsync(task)
                : await this._runService.api.updateTaskAsync(this._taskId, task);
            if (!result.isOk) {
                this._toaterService.showNetworkError(result.err);
            } else {
                //history.back();
                Router.go(`/space/${this._userState.selectedSpaceId}/config/${this._runModel.id}/details/schedules`);
            }

        }
    }

    /*async updateInputParams() {
        //convert array input params liveValues to object with ket values.
        const convertedObject = {};

        if (this._inputParametersEditor) {
            for (const item of this._inputParametersEditor.liveValue) {
                convertedObject[item.name] = item.value;
            }
            //compare and updated this._parameters data with updated input params live values.
            for (const key in this._parameters) {
                if (convertedObject.hasOwnProperty(key)) {
                    this._parameters[key] = convertedObject[key];
                }
            }
        }
    }*/

    async RunAsync() {
        if (this.reportValidity()) {
            if (!this._isProxyRequired && (!this._selectedProxyPoolId || this._selectedProxyPoolId === -1) && !this._runModel.proxyPoolId) {
                const dialogRes = await this._modalService.openConfirmDialogAsync({
                    saveCaption: "Run Now",
                    title: "Run Without Proxy?",
                    body: "Are you sure you want to run this agent without setting a proxy? The agent has no default proxies, so it will run without proxies.",
                });
                if (!dialogRes.isSave) {
                    return;
                }
            }

            this._inputParametersEditor?.save();
            const groupId = parseInt(this._serverEditor.liveValue);
            const taskName =
                this._task?.taskName ??
                this._runModel?.name +
                    " - " +
                    new Date()
                        .toLocaleString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "numeric", minute: "numeric" })
                        .replace(",", "");
            const run = {
                configId: this._runModel.id,
                proxyPoolId: this._runModel.proxyPoolId !== this._selectedProxyPoolId ? this._selectedProxyPoolId : -1,
                parallelism: this._isParallelism ? this._parallelSetsEditor.liveValue ?? 2 : 1,
                ParallelExport: this._isParallelism ? (this._parallelExportEditor.liveValue as ParallelExport) : undefined,
                ParallelMaxConcurrency: this._isParallelism ? this._concurrencyEditor.liveValue : undefined,
                serverGroupId: groupId > 0 ? groupId : undefined,
                parameters: JSON.stringify(this._inputParametersEditor?.properties ?? {}),
                isSaveAsTask: false,
                taskName: taskName,
                isExclusive: this._exclusiveEditor.liveValue,
                isWaitOnFailure: this._waitOnFailureEditor.liveValue,
                logLevel: this._logLevelEditor.liveValue as LogLevel,
                logMode: this._logModeEditor.liveValue as LogMode,
            };
            const result = await this._runService.api.createAsync(run);
            if (!result.isOk) {
                this._toaterService.showNetworkError(result.err);
            } else {
                //history.back();
                Router.go(`/space/${this._userState.selectedSpaceId}/config/${this._runModel.id}/details/runs`);
                    }
        }
    }

    cancel() {
             history.back();
    }
    selectProxyPoolId() {
        this._selectedProxyPoolId = Number(this._proxyPoolsSelect.liveValue);

        //force ui update
        this.requestUpdate();
    }
    updateRunEveryPeriod() {
        this._runEveryPeriodValue = this._runEveryPeriod.liveValue;
    }
    cronUpdated() {
        try {
            this._CRONString = cronstrue.toString(
                this.toCron(
                    this._CRONMinute.liveValue,
                    this._CRONHour.liveValue,
                    this._CRONDay.liveValue,
                    this._CRONMonth.liveValue,
                    this._CRONWeekDay.liveValue
                )
            );
        } catch {
            this._CRONString = "Invalid CRON String";
        }
    }

    scheduleChange() {
        if (this._scheduleOnOff.liveValue == false) {
            this._showscheduleType = false;
            this._runOnceOption = false;
            this._runEveryOption = false;
            this._CRONOption = false;
        } else {
            if (this._scheduleType.liveValue === "1") {
                this._showscheduleType = true;
                this._runOnceOption = true;
                this._runEveryOption = false;
                this._CRONOption = false;
            } else if (this._scheduleType.liveValue === "2") {
                this._showscheduleType = true;
                this._runOnceOption = false;
                this._runEveryOption = true;
                this._CRONOption = false;
            } else if (this._scheduleType.liveValue === "3") {
                this._showscheduleType = true;
                this._runOnceOption = false;
                this._runEveryOption = false;
                this._CRONOption = true;
            }
        }
    }

    scheduleChangeEdit(scheduleType) {
        if (scheduleType == 0) {
            this._showscheduleType = false;
            this._runOnceOption = false;
            this._runEveryOption = false;
            this._CRONOption = false;
        } else {
            if (scheduleType == 1) {
                this._showscheduleType = true;
                this._runOnceOption = true;
                this._runEveryOption = false;
                this._CRONOption = false;
            } else if (scheduleType == 2) {
                this._showscheduleType = true;
                this._runOnceOption = false;
                this._runEveryOption = true;
                this._CRONOption = false;
            } else if (scheduleType == 3) {
                this._showscheduleType = true;
                this._runOnceOption = false;
                this._runEveryOption = false;
                this._CRONOption = true;
            }
        }
    }
    get12HoursFormat(today) {
        return String(today.getHours() > 12 ? today.getHours() - 12 : today.getHours()).padStart(2, "0");
    }
    getMinsFormat(today) {
        return String(today.getMinutes()).padStart(2, "0");
    }
    gettimePMAM(today) {
        return String(today.getHours() >= 12 ? "12" : "0");
    }

    private parallelismChanged() {
        this._isParallelism = this._isParallelismEditor.liveValue;
        if (this._isParallelism && (this._parallelSetsEditor.value ?? 1) < 2) {
            this._parallelSetsEditor.value = 2;
        }
    }

    // Updated handler to manage validity state and visuals
    private _handleStartDateChange() {
        if (this._StartDate) { // Check if the element exists
             const selectedDate = this._StartDate.liveValue;
             const now = new Date();

             // Determine if the date is in the past
             this._isStartDateInPast = selectedDate ? selectedDate.getTime() < now.getTime() : false;

             // Set the custom validity message based on the condition
             if (this._isStartDateInPast) {
                 this._StartDate.setCustomValidity("Start date and time must be in the future.");
             } else {
                 this._StartDate.setCustomValidity(""); // Clear validity message if date is valid or not set
             }

             // Check the current validity state
             const isValid = this._StartDate.checkValidity();

             // Update the visual indicator (e.g., red border)
             this._StartDate.toggleInvalidVisuals(!isValid);

             // Request an update to re-render the warning message if needed
             this.requestUpdate('_isStartDateInPast');

        } else {
             this._isStartDateInPast = false;
             this.requestUpdate('_isStartDateInPast');
        }
    }


    render() {
        const runOnceOptionsStyle = { display: this._runOnceOption ? "flex" : "none", gap: "10px", "flex-direction": "column" };
        const runEveryOptionsStyle = { display: this._runEveryOption ? "flex" : "none", gap: "10px", "flex-direction": "column" };
        const CRONOptionsStyle = { display: this._CRONOption ? "flex" : "none", gap: "10px", "flex-direction": "column" };

        const scheduleOptionsStyle = { display: this._showscheduleType ? "flex" : "none", gap: "10px" };
        const startDateOptionsStyle = {
                display: this._showscheduleType ? "flex" : "none",
                gap: "10px", // Adjusted gap for warning message
                "flex-direction": "column",
        };

        // Add none option to proxy pools
        const proxyPools = this._proxyPools.map((p) => ({ id: p.id, name: p.name }));
        const noneOption = [{ id: -1, name: "None" }];
        const proxyPoolOptions = this._authService.isSE4Admin
            ? this._isProxyRequired
                ? proxyPools
                : noneOption.concat(proxyPools)
            : proxyPools;
        const taskName =
            this._task?.taskName ??
            this._runModel?.name +
                " - " +
                new Date()
                    .toLocaleString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "numeric", minute: "numeric" })
                    .replace(",", "");

        //get the proxy pool from the proxyPoolOptions based on the id matching the _selectedProxyPoolId
        const selectedProxyPool = this._proxyPools.find((pool) => pool.id === this._selectedProxyPoolId);
        const proxyCost = selectedProxyPool != null && selectedProxyPool.displayPrice != null ? selectedProxyPool.displayPrice.toFixed(2) : 0;

        return html`
            <se-loading-panel .isLoading=${this._isLoading}>
                 <div class="breadcrumbs">
                    <a href="/org/spaces">Spaces</a>
                    <span>/</span>
                    <a href="/space/${this._userState.selectedSpaceId}">${this._spaceName ?? '...'}</a>
                    <span>/</span>
                    <a href="/space/${this._userState.selectedSpaceId}/config/${this._configId}/details/info">${this._configName ?? '...'}</a>
                    <span>/</span>
                    <a>${this._mode === 'run' ? 'Run' : 'Schedule'} Setup</a>
                </div>
                <div class="h2">${this._mode === 'run' ? 'Run' : 'Schedule'} ${this._runModel?.name ?? ""}</div>

                <div style="display: flex; flex-direction: column; gap: 15px;">

                    <!-- Schedule Run (Conditional) -->
                    ${when(this._mode === 'schedule', () => html`
                        <se-edit-form-card cardTitle="Schedule Run" collapsible>
                             <se-form-input-editor
                                id="taskName"
                                type="text"
                                label="Name"
                                labelHelp="A descriptive name for this scheduled task"
                                input-type="text"
                                required
                                size="30"
                                .value=${!this._isNew ? this.__taskName : taskName}
                            ></se-form-input-editor>

                             <se-form-toggle-editor
                                id="scheduleOnOff"
                                .value="${!this._isNew ? (this.__scheduleType === 0 ? false : true) : true}"
                                name="Schedule"
                                label="Enable Schedule"
                                labelHelp="Enable or disable the schedule for this task"
                                @valueChanged=${this.scheduleChange}
                            ></se-form-toggle-editor>

                            <div style=${styleMap(startDateOptionsStyle)}>
                                 <se-form-datetime-editor
                                    id="StartDate"
                                    .value=${this.__today} /* Bind value directly */
                                    label="Start Date & Time"
                                    labelHelp="The date and time when the schedule should start"
                                    type="datetime-local"
                                    @valueChanged=${this._handleStartDateChange} /* Handler remains on valueChanged */
                                    .min=${new Date().toISOString().slice(0, 16)} /* Set min to prevent browser validation conflict */
                                ></se-form-datetime-editor>
                                 ${when(this._isStartDateInPast, () => html`
                                    <div class="past-date-warning">
                                        <fa-icon fa-class="fas fa-triangle-exclamation" color="orange"></fa-icon>
                                        Warning: Start date is in the past. Ensure it's correct before saving.
                                    </div>
                                `)}
                            </div>

                             <div style=${styleMap(scheduleOptionsStyle)}>
                                <se-form-select-editor
                                    style="width: 100%;"
                                    .value="${!this._isNew ? String(this.__scheduleType) : "1"}"
                                    id="scheduleType"
                                    name="ScheduleType"
                                    label="Frequency"
                                    labelHelp="Choose how often the task should run"
                                    @valueChanged=${this.scheduleChange}
                                    .options=${[
                                        { id: 1, name: "Run Once" },
                                        { id: 2, name: "Run Every" },
                                        { id: 3, name: "CRON" },
                                    ]}
                                >
                                </se-form-select-editor>
                            </div>

                            <div style=${styleMap(runOnceOptionsStyle)}></div>

                            <div style=${styleMap(runEveryOptionsStyle)}>
                                <se-form-select-editor
                                    id="runEveryPeriod"
                                    .value="${!this._isNew ? String(this.__runEveryPeriod) : "1"}"
                                    label="Run Every"
                                    labelHelp="Interval count and unit"
                                    @valueChanged=${this.updateRunEveryPeriod}
                                    .options=${[
                                        { id: 1, name: "minutes" },
                                        { id: 2, name: "hours" },
                                        { id: 3, name: "days" },
                                        { id: 4, name: "weeks" },
                                        { id: 5, name: "months" },
                                    ]}
                                >
                                    <se-number-editor
                                        slot="before-input"
                                        id="runEveryCount"
                                        .value=${!this._isNew ? this.__runEveryCount : 1}
                                        min="1"
                                        required
                                        label=""
                                        inputWidth="60px"
                                        @valueChanged=${this._validateRunEveryCount}
                                    ></se-number-editor>
                                </se-form-select-editor>
                            </div>

                            <div style=${styleMap(CRONOptionsStyle)}>
                                 <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(80px, 1fr)); gap: 10px;">
                                    <se-form-input-editor
                                        @valueChanged=${this.cronUpdated}
                                        .value="${!this._isNew && this._task?.scheduleType == 3 ? this.__localSchedule?.[0] ?? '*' : '*'}"
                                        id="CRONMinute"
                                        label="Minute"
                                        labelHelp="0-59, *"
                                        input-type="text"
                                    ></se-form-input-editor>
                                    <se-form-input-editor
                                        @valueChanged=${this.cronUpdated}
                                        .value="${!this._isNew && this._task?.scheduleType == 3 ? this.__localSchedule?.[1] ?? '*' : '*'}"
                                        id="CRONHour"
                                        label="Hour"
                                        labelHelp="0-23, *"
                                        input-type="text"
                                    ></se-form-input-editor>
                                    <se-form-input-editor
                                        @valueChanged=${this.cronUpdated}
                                        .value="${!this._isNew && this._task?.scheduleType == 3 ? this.__localSchedule?.[2] ?? '*' : '*'}"
                                        id="CRONDay"
                                        label="Day (Month)"
                                        labelHelp="1-31, *"
                                        input-type="text"
                                    ></se-form-input-editor>
                                    <se-form-input-editor
                                        @valueChanged=${this.cronUpdated}
                                        .value="${!this._isNew && this._task?.scheduleType == 3 ? this.__localSchedule?.[3] ?? '*' : '*'}"
                                        id="CRONMonth"
                                        label="Month"
                                        labelHelp="1-12, *"
                                        input-type="text"
                                    ></se-form-input-editor>
                                    <se-form-input-editor
                                        @valueChanged=${this.cronUpdated}
                                        .value="${!this._isNew && this._task?.scheduleType == 3 ? this.__localSchedule?.[4] ?? '*' : '*'}"
                                        id="CRONWeekDay"
                                        label="Day (Week)"
                                        labelHelp="0-6, *"
                                        input-type="text"
                                    ></se-form-input-editor>
                                </div>
                            </div>
                        </se-edit-form-card>
                    `)}



                    <!-- Card 1: Run Options -->
                    <se-edit-form-card cardTitle="Run Options">
                        <se-form-select-editor
                            id="proxyPools"
                            name="Proxy Pools"
                            label="Proxy"
                            labelHelp="Select the proxy pool to use for this ${this._mode}. Cost: $${proxyCost} per GB"
                            @valueChanged=${this.selectProxyPoolId}
                            .options=${proxyPoolOptions}
                            .value=${this._selectedProxyPoolId.toString()}
                            ?required=${this._isProxyRequired}
                        >
                        </se-form-select-editor>

                        <se-form-toggle-editor
                            id="isParallelism"
                            ?value=${this._isParallelism}
                            label="Parallel Processing"
                            labelHelp="Split up inputs and process in parallel"
                            @valueChanged=${this.parallelismChanged}
                        ></se-form-toggle-editor>

                        <div style=${styleMap({ display: this._isParallelism ? "block" : "none" })}>
                            <se-form-number-editor
                                id="parallelSets"
                                .value="${this._selectedParallelism}"
                                min="2"
                                label="Total runs"
                                labelHelp="Number of parallel runs to execute"
                                input-type="number"
                                placeholder="2"
                            ></se-form-number-editor>
                            <se-form-number-editor
                                id="concurrency"
                                .value="${this._selectedParallelMaxConcurrency}"
                                min="1"
                                label="Concurrency"
                                labelHelp="Maximum number of runs to execute simultaneously (blank for unlimited)"
                                input-type="number"
                                placeholder="Unlimited"
                            ></se-form-number-editor>
                            <se-form-select-editor
                                id="parallelExport"
                                label="Export"
                                labelHelp="How to handle exports from parallel runs"
                                .value="${this._selectedParallelExport}"
                                .options=${this._parallelExportOptions}
                            ></se-form-select-editor>
                        </div>
                    </se-edit-form-card>

                    <!-- Card 2: Input Parameters (Collapsible, Collapsed by Default) -->
                     ${when(
                        this._runModel && Object.keys(this._runModel.inputParameters).length > 0,
                        () => html`
                            <se-edit-form-card cardTitle="Input Parameters" collapsible>
                                 <se-parameter-editor
                                    id="inputParameters"
                                    .properties=${this._runModel.inputParameters}
                                    .addedProperties=${this._taskProperties}
                                    label="" /* Label provided by card title */
                                ></se-parameter-editor>
                            </se-edit-form-card>`
                        )}


                    <!-- Card 3: Advanced (Collapsible, Collapsed by Default) -->
                    <se-edit-form-card cardTitle="Advanced" collapsible collapsed>
                         <se-form-toggle-editor
                            id="exclusive"
                            ?value=${true}
                            label="Run one at a time"
                            labelHelp="Prevent other runs of this config while this one is active"
                        ></se-form-toggle-editor>
                         <se-form-toggle-editor
                            id="waitOnFailure"
                            ?value=${false}
                            label="Wait on failure"
                            labelHelp="If a parallel run fails, wait for others before marking the job failed"
                        ></se-form-toggle-editor>
                         <se-form-select-editor
                            id="logMode"
                            name="logMode"
                            label="Log mode"
                            labelHelp="Choose the format for the logs"
                            .value="${this._selectedLogMode}"
                            .options=${this._logModes}
                        ></se-form-select-editor>
                        <se-form-select-editor
                            id="logLevel"
                            name="logLevel"
                            label="Log level"
                            labelHelp="Select the minimum level of detail for logs"
                            .value="${this._selectedLogLevel}"
                            .options=${this._logLevels}
                        ></se-form-select-editor>
                         <se-form-select-editor
                            id="server"
                            name="server"
                            label="Server group"
                            labelHelp="Assign this ${this._mode} to a specific server group"
                            .value="${this._selectedServerGroupId?.toString()}"
                            .options=${this._serverGroups}
                        ></se-form-select-editor>
                    </se-edit-form-card>

                     <!-- Buttons for Running Now (Conditional) -->
                     ${when(this._mode === 'run', () => html`
                         <se-card-buttons>
                            <se-primary-button .action="${() => this.RunAsync()}" text="Run Now"></se-primary-button>
                            <se-secondary-button @click="${this.cancel}" text="Cancel"></se-secondary-button>
                        </se-card-buttons>
                     `)}



                     <!-- Buttons for Saving Schedule (Conditional) -->
                     ${when(this._mode === 'schedule', () => html`
                         <se-card-buttons>
                            <se-primary-button .action="${() => this.saveTaskAsync()}" text="${this._isNew ? "Save Schedule" : "Update Schedule"}"></se-primary-button>
                            <se-secondary-button @click="${this.cancel}" text="Cancel"></se-secondary-button>
                        </se-card-buttons>
                     `)}

                </div>
            </se-loading-panel>
        `;
    }

    static styles = css`
        :host {
            display: flex;
            flex-direction: column;
            padding: 15px;
            box-sizing: border-box;
            gap: 15px; /* Add gap between elements */
        }
         .breadcrumbs {
            display: flex;
            flex-direction: row;
            gap: 10px;
            font: var(--font-smaller);
            margin-bottom: 20px;
        }
        .breadcrumbs a {
            color: var(--color-primary);
            text-decoration: none;
        }
        .breadcrumbs a:hover {
            text-decoration: underline;
        }
        .h2 {
            font: var(--font-h2);
            font-weight: 600;
            margin-bottom: 10px;
        }
        .h3 {
            font: var(--font-h3);
        }
        se-edit-form-card {
            width: 100%; /* Make cards take full width */
        }
        /* Style for the link button */
        .link-button {
            text-decoration: none;
            white-space: nowrap;
        }
        .link-button:hover {
            text-decoration: underline;
        }

         /* Instructions text style */
        .instructions {
            font-size: var(--font-size-small);
            color: var(--color-gray-6);
            padding: 0 10px 10px 10px; /* Add padding */
            margin: 0; /* Remove default margin */
            border-bottom: 1px solid var(--color-gray-4); /* Add separator */
            margin-bottom: 10px; /* Space below separator */

        }
        div[style*="CRONOptionsStyle"] .instructions { /* Style for CRON description */
             border-bottom: none;
             margin-bottom: 0;
             padding: 5px 10px 0 10px;
        }

         /* Remove default margin from parameter editor label if inside card */
         se-parameter-editor::part(label) { /* Might need adjustment based on se-parameter-editor structure */
             margin-bottom: 0;
         }

        /* Ensure card buttons have spacing */
         se-card-buttons {
             margin-top: 5px; /* Add space above button rows */
             padding: 0; /* Reset padding if needed */
         }

         div[style*="scheduleOptionsStyle"],
        div[style*="startDateOptionsStyle"],
        div[style*="runOnceOptionsStyle"],
        div[style*="runEveryOptionsStyle"],
        div[style*="CRONOptionsStyle"] {
            padding: 0 10px;
        }
        div[style*="runEveryOptionsStyle"] > div { /* Target inner div */
             align-items: end; /* Align Run Every inputs */
             gap: 10px;
        }
        div[style*="CRONOptionsStyle"] > div:first-of-type {
             display: grid;
             grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
             gap: 10px;
             padding-bottom: 5px; /* Add space below inputs */
        }
        div[style*="CRONOptionsStyle"] > se-form-input-editor {
             margin-bottom: 5px;
        }

         /* Style for the past date warning */
         .past-date-warning {
             font-size: var(--font-size-small);
             color: var(--color-warning-text); /* Use a warning color */
             display: flex;
             align-items: center;
             gap: 5px;
             margin-top: -5px; /* Adjust spacing */
             padding-left: 10px; /* Align with input */
         }


    `;
}
